import { useEffect, useState } from 'react';
import type { PortalBootstrapData } from '../types/window';
import { __ } from '@/hub-shared/utils/i18n';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';
import { vsmeDossierPath } from '@tpct/mission-core/utils/vsmePortalRoutes';

type LegacyVsmeReportDossierRedirectProps = {
  bootstrap: PortalBootstrapData;
  reportId: number;
};

type ReportsListPayload = {
  reports?: Array<{ id: number; missionId?: number | null }>;
};

const LegacyVsmeReportDossierRedirect = ({
  bootstrap,
  reportId,
}: LegacyVsmeReportDossierRedirectProps) => {
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const run = async () => {
      try {
        const response = await fetchWithNonce(`${bootstrap.restUrls.vsme}reports`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-WP-Nonce': bootstrap.nonce,
          },
        });

        if (!response.ok) {
          if (active) {
            setError(await parseApiError(response));
          }
          return;
        }

        const payload = (await response.json()) as { success?: boolean; data?: ReportsListPayload };
        const reports = payload.data?.reports ?? [];
        const match = reports.find((report) => report.id === reportId);
        const missionId = match?.missionId ?? null;

        if (!active) {
          return;
        }

        if (missionId === null || missionId <= 0) {
          setError(__('Aucune mission VS n’est rattachée à ce rapport.'));
          return;
        }

        const search = window.location.search;
        const target = `${vsmeDossierPath(missionId, bootstrap.portalPath)}${search}`;
        window.location.replace(target);
      } catch {
        if (active) {
          setError(__('Impossible de rediriger vers le dossier de travail.'));
        }
      }
    };

    void run();

    return () => {
      active = false;
    };
  }, [bootstrap.nonce, bootstrap.portalPath, bootstrap.restUrls.vsme, reportId]);

  return (
    <section aria-live="polite">
      <h2>{__('Redirection...')}</h2>
      {error !== '' ? (
        <div className="accounting-hub-notice" role="alert">
          <p>{error}</p>
        </div>
      ) : (
        <p>{__('Redirection vers le dossier de travail...')}</p>
      )}
    </section>
  );
};

export default LegacyVsmeReportDossierRedirect;
